import { ref, watch, onMounted, onUnmounted } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'

import { getI18n } from '@/plugins/i18n'

import httpApi from '@/apis/http.api'
import {composeUrl} from '@/apis/base.api'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'

import { useNotify } from '@/composables/notify'

export const getModelLockedIds = (entityLocks, modelName, byMe = false, userId = null) => {
  let keys = Object.keys(entityLocks).filter(key => key.split('.')[0] === modelName)
  if (byMe && userId) {
    keys = keys.filter(key => entityLocks[key]?.user_id === userId)
  }
  return keys.map(key => key.split('.')[1]).filter(Boolean)
}

export const getReferenceLinkText = (notificationInternalMessage) => {
  const i18n = getI18n()
  const cacheKey = notificationInternalMessage.cache_key
  let text = i18n.t(notificationInternalMessage.reference_link_data.text) + ' ' + notificationInternalMessage.reference_link_data.identifier_values.join(' ')
  if (cacheKey.split('.')[1] === 'Product' || cacheKey.split('.')[1] === 'DiscountCategory') {
    const registryStore = useRegistryStore()
    const supplierBrandCode = registryStore.supplierBrands.find(i => i.id === Number(notificationInternalMessage.reference_link_data.identifier_values[0])).code
    text = i18n.t(notificationInternalMessage.reference_link_data.text) + ' ' + supplierBrandCode + ' ' + notificationInternalMessage.reference_link_data.identifier_values[1]
  }
  if (cacheKey.split('.')[1] === 'SubjectDiscount') {
    const registryStore = useRegistryStore()
    const supplierBrandCode = registryStore.supplierBrands.find(i => i.id === Number(notificationInternalMessage.reference_link_data.identifier_values[0])).code
    const subjectCode = registryStore.getSubjects().find(i => i.id === Number(notificationInternalMessage.reference_link_data.identifier_values[1]))?.code
    if (subjectCode) {
      text = i18n.t(notificationInternalMessage.reference_link_data.text) + ' ' + supplierBrandCode + ' ' + subjectCode
    } else {
      text = i18n.t(notificationInternalMessage.reference_link_data.text) + ' ' + supplierBrandCode
    }
  }
  if (cacheKey.split('.')[1] === 'SaleDocument') {
    text = i18n.t(notificationInternalMessage.reference_link_data.text) + ' ' + notificationInternalMessage.reference_link_data.identifier_values.slice(0, 2).join(' ')
  }

  return text
}

export const useEntityLock = ({
  baseLockUrl,
  modelName,
  masterModelName = null,
  gridRef
}) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { notifyInfo } = useNotify()
  const userId = webSiteStore.user?.id
  const getFocusedRowId = () => gridRef.value?.instance.masterFocusedRowKey || gridRef.value?.instance.focusedRowKey
  const getLockUrl = (id) => composeUrl(baseLockUrl, `${id || getFocusedRowId()}/lock/`)
  const getRequestUnlockUrl = (id) => composeUrl(baseLockUrl, `${id || getFocusedRowId()}/request-unlock/`)
  const isReadOnly = ref(false)
  const formInstance = ref(null)
  const releasedByUser = ref(false)

  const onBeforeUnloadAsync = async() => {
    const lockedId = getCurrentlyLockedId() || getFocusedRowId()
    const lockUrl = getLockUrl(lockedId)
    if (isLocked(true, lockedId, true) && lockUrl && lockedId) {
      const authHeader = httpApi.defaults.headers.common['Authorization']
      await fetch(lockUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader ? { Authorization: authHeader } : {})
        },
        body: JSON.stringify({ lock: false }),
        keepalive: true
      })
    }
  }

  const isLocked = (byMe = false, entityId = null, addCurrentlyLocked = false) => {
    entityId = entityId || getFocusedRowId()
    entityId = String(entityId)
    const lockModelName = masterModelName || modelName
    const modelLockedIds = getModelLockedIds(
      webSiteStore.entityLocks,
      lockModelName,
      byMe,
      userId
    )
    if (addCurrentlyLocked && webSiteStore.currentlyLockedEntity?.lockedKey?.split('.')[1] === lockModelName) {
      modelLockedIds.push(webSiteStore.currentlyLockedEntity.lockedKey.split('.')[2])
    }

    return modelLockedIds.includes(entityId)
  }

  const createFn = (url, param = {}) => async() => {
    const authHeader = httpApi.defaults.headers.common['Authorization']
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {})
      },
      body: JSON.stringify(param),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const text = await response.text()
    return text ? JSON.parse(text) : null
  }

  const unlockRequestConfirmation = (id) => {
    const requestUnlockUrl = getRequestUnlockUrl(id)
    const requestUnlockFn = createFn(requestUnlockUrl)
    notifyInfo(i18n.t('common.entityLockRequestConfirmation'), {
      timeout: 0,
      actions: [
        { label: i18n.t('common.confirm'), color: 'white', handler: async() => await requestUnlockFn(id) },
        { label: i18n.t('common.cancel'), color: 'white', handler: () => {} }]
    })
  }

  const entityName = masterModelName || modelName

  const getCurrentlyLockedId = () => {
    const lockedKeyParts = webSiteStore.currentlyLockedEntity?.lockedKey?.split('.')
    if (lockedKeyParts?.length === 3 && lockedKeyParts[1] === entityName) {
      return lockedKeyParts[2]
    }
    return null
  }

  const lockEntity = async(id) => {
    id = id || getFocusedRowId()
    if (isLocked(false, id)) {
      return
    }
    const lockUrl = getLockUrl(id)
    const lockFn = createFn(lockUrl, { lock: true })
    try {
      const data = await lockFn()
      releasedByUser.value = false
      webSiteStore.currentlyLockedEntity = {
        lockedKey: data.locked_key,
        refreshLockUrl: data.refresh_lock_url
      }
    } catch (error) {
      console.error(error)
    }
  }

  const releaseLock = async(id) => {
    try {
      id = id || getCurrentlyLockedId() || getFocusedRowId()
      if (isLocked(true, id, true) && !isReadOnly.value) {
        const lockUrl = getLockUrl(id)
        const unlockFn = createFn(lockUrl, { lock: false })
        await unlockFn()
        releasedByUser.value = true
        webSiteStore.currentlyLockedEntity = null
      }
    } catch (error) {
      console.error(error)
    } finally {
      isReadOnly.value = false
    }
  }

  const updateToolbarItems = (formInstance, oldEntityId = null, hasClaim = null) => {
    const toolbarItems = formInstance.option('toolbarItems') || []
    const updatedToolbarItems = toolbarItems.map((item, index) => {
      if (index === toolbarItems.length - 1) return item
      return { ...item, visible: (!hasClaim || hasClaim()) && !isReadOnly.value }
    })

    if (isReadOnly.value && oldEntityId) {
      const rowIndex = gridRef.value.instance.getRowIndexByKey(oldEntityId)
      updatedToolbarItems.unshift({
        widget: 'dxButton',
        toolbar: 'bottom',
        location: 'after',
        options: {
          text: i18n.t('common.reload'),
          icon: 'refresh',
          type: 'default',
          onClick: () => {
            if (gridRef) {
              gridRef.value.instance.cancelEditData()
              gridRef.value.instance.refresh().then(() => {
                gridRef.value.instance.editRow(rowIndex)
              })
            }
          }
        }
      })
    }

    return updatedToolbarItems
  }

  const readOnlyToolbarItems = (e, hasClaim) => {
    formInstance.value = e.component
    const updatedToolbarItems = updateToolbarItems(e.component, null, hasClaim)
    e.component.option('toolbarItems', updatedToolbarItems)
  }

  const onEntityLockMounted = () => {
    window.addEventListener('beforeunload', onBeforeUnloadAsync)
  }

  const onEntityLockUnmounted = () => {
    window.removeEventListener('beforeunload', onBeforeUnloadAsync)
  }

  const getEntityLockButtons = (options) => {
    const i18n = getI18n()
    return [
      {
        name: 'edit',
        text: i18n.t('common.edit'),
        icon: 'edit',
        onClick: async({ grid, entityId }) => {
          grid.instance.editRow(grid.instance.getRowIndexByKey(entityId))
        },
        visibleFn: (e) => (!options?.hasClaim || options.hasClaim()) && (options?.visible?.edit ? options.visible.edit(e) : !isLocked(false, e.entityId)),
      },
      {
        name: 'delete',
        text: i18n.t('common.delete'),
        icon: 'trash',
        onClick: async({ grid, entityId }) => {
          if (options?.custom_text?.delete) {
            const { confirm } = await import('devextreme/ui/dialog')
            const rowIndex = grid.instance.getRowIndexByKey(entityId)
            const rowData = grid.instance.getVisibleRows().find(r => r.key === entityId)?.data
            const result = await confirm(options.custom_text.delete(rowData), i18n.t('common.confirm'))
            if (result) {
              grid.instance.option('editing.confirmDelete', false)
              grid.instance.deleteRow(rowIndex)
            }
          } else {
            grid.instance.deleteRow(grid.instance.getRowIndexByKey(entityId))
          }
        },
        visibleFn: (e) => (!options?.hasClaim || options.hasClaim()) && (options?.visible?.delete ? options.visible.delete(e) : !isLocked(false, e.entityId))
      },
      {
        name: 'view',
        text: i18n.t('common.view'),
        icon: 'search',
        onClick: async({ grid, entityId }) => {
          grid.instance.editRow(grid.instance.getRowIndexByKey(entityId))
        },
        visibleFn: (e) => !(!options?.hasClaim || options.hasClaim()) || (options?.visible?.view ? options.visible.view(e) : isLocked(false, e.entityId)),
      },
    ]
  }

  // Auto configure
  onMounted(() => onEntityLockMounted())
  onUnmounted(() => onEntityLockUnmounted())
  onBeforeRouteLeave((to, from, next) => {
    onBeforeUnloadAsync()
    next()
  })
  watch(() => webSiteStore.currentlyLockedEntity, (newVal, oldVal) => {
    if (newVal === null && !releasedByUser.value) {
      isReadOnly.value = true
      const oldEntityId = Number(oldVal?.lockedKey?.split('.').pop())
      if (formInstance?.value && oldEntityId) {
        const updatedToolbarItems = updateToolbarItems(formInstance.value, oldEntityId)
        formInstance.value?.option('toolbarItems', updatedToolbarItems)
      }
      return
    }
  })

  return {
    unlockRequestConfirmation,
    lockEntity,
    releaseLock,
    readOnlyToolbarItems,
    isLocked,
    onEntityLockMounted,
    onEntityLockUnmounted,
    onBeforeUnloadAsync,
    isReadOnly,
    getEntityLockButtons,
    entityName
  }
}
